import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Map, {
  Layer,
  Marker,
  Source,
  type MapLayerMouseEvent,
  type MapRef,
} from 'react-map-gl/maplibre';
import { METRO_BBOX, bboxOfPolygon, polygonAreaKmSq, type GeoPolygon } from '@soc/shared';
import { AddressSearch } from './AddressSearch';
import { formatAreaKmSq } from './formUi';

const MAP_STYLE = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json';
const ACCENT = '#00e5ff';
const DANGER = '#ff5d73';

type Pt = [number, number];

/** Open working ring (closing vertex dropped) from a GeoPolygon. */
function ringPoints(poly: GeoPolygon | null | undefined): Pt[] {
  const ring = poly?.coordinates[0] ?? [];
  if (ring.length === 0) return [];
  const first = ring[0];
  const last = ring[ring.length - 1];
  const closed = ring.length > 1 && first[0] === last[0] && first[1] === last[1];
  return (closed ? ring.slice(0, -1) : ring).map(([lng, lat]) => [lng, lat] as Pt);
}

function toPolygon(points: Pt[]): GeoPolygon | null {
  if (points.length < 3) return null;
  return { type: 'Polygon', coordinates: [[...points, points[0]]] };
}

/**
 * Click-to-draw polygon editor (vertices, Undo/Clear) with three additions over
 * the bare DrawMap: an optional read-only `boundary` overlay, an `invalid` flag
 * that recolors the polygon red, and an embedded address search that imports a
 * boundary. The parent owns validity (boundary containment + budget) and passes
 * `invalid` back in; this component just draws + reports geometry.
 */
export function PolyMap({
  initialPolygon,
  onChange,
  boundary,
  invalid = false,
  invalidMessage,
}: {
  initialPolygon?: GeoPolygon | null;
  onChange: (polygon: GeoPolygon | null) => void;
  boundary?: GeoPolygon | null;
  invalid?: boolean;
  invalidMessage?: string;
}) {
  const [points, setPoints] = useState<Pt[]>(() => ringPoints(initialPolygon));
  const mapRef = useRef<MapRef | null>(null);
  const fittedRef = useRef(false);

  const polygon = useMemo(() => toPolygon(points), [points]);

  useEffect(() => {
    onChange(polygon);
  }, [polygon, onChange]);

  const fitTo = useCallback((poly: GeoPolygon | null | undefined) => {
    const map = mapRef.current;
    if (!map || !poly) return;
    try {
      const b = bboxOfPolygon(poly);
      map.fitBounds(
        [
          [b.minLng, b.minLat],
          [b.maxLng, b.maxLat],
        ],
        { padding: 48, duration: 600, maxZoom: 14 },
      );
    } catch {
      /* empty ring — ignore */
    }
  }, []);

  // Fit to the initial polygon (edit) or the boundary (project) once, on first load.
  const handleLoad = useCallback(() => {
    if (fittedRef.current) return;
    fittedRef.current = true;
    fitTo(initialPolygon ?? boundary ?? null);
  }, [fitTo, initialPolygon, boundary]);

  const handleClick = useCallback((e: MapLayerMouseEvent) => {
    const { lng, lat } = e.lngLat;
    setPoints((prev) => [...prev, [lng, lat]]);
  }, []);

  const importPolygon = useCallback(
    (poly: GeoPolygon) => {
      setPoints(ringPoints(poly));
      fitTo(poly);
    },
    [fitTo],
  );

  const color = invalid ? DANGER : ACCENT;
  const area = polygon ? polygonAreaKmSq(polygon) : 0;

  const lineData: GeoJSON.Feature = {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'LineString',
      coordinates: points.length ? [...points, ...(polygon ? [points[0]] : [])] : [],
    },
  };

  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      <Map
        ref={mapRef}
        mapStyle={MAP_STYLE}
        initialViewState={{ longitude: METRO_BBOX.centerLng, latitude: METRO_BBOX.centerLat, zoom: 7 }}
        style={{ position: 'absolute', inset: 0 }}
        onClick={handleClick}
        onLoad={handleLoad}
        cursor="crosshair"
      >
        {/* read-only org boundary overlay */}
        {boundary && (
          <Source id="boundary" type="geojson" data={boundary as unknown as GeoJSON.Geometry}>
            <Layer id="boundary-fill" type="fill" paint={{ 'fill-color': '#aeb9c7', 'fill-opacity': 0.04 }} />
            <Layer
              id="boundary-line"
              type="line"
              paint={{ 'line-color': '#aeb9c7', 'line-width': 1.5, 'line-opacity': 0.5, 'line-dasharray': [2, 2] }}
            />
          </Source>
        )}

        {polygon && (
          <Source id="draw-fill" type="geojson" data={polygon as unknown as GeoJSON.Geometry}>
            <Layer id="draw-fill-layer" type="fill" paint={{ 'fill-color': color, 'fill-opacity': 0.14 }} />
          </Source>
        )}
        {points.length > 0 && (
          <Source id="draw-line" type="geojson" data={lineData}>
            <Layer id="draw-line-layer" type="line" paint={{ 'line-color': color, 'line-width': 2 }} />
          </Source>
        )}
        {points.map((p, i) => (
          <Marker key={i} longitude={p[0]} latitude={p[1]}>
            <span
              style={{
                display: 'block',
                width: 9,
                height: 9,
                borderRadius: '50%',
                background: color,
                border: '2px solid #06222b',
                boxShadow: `0 0 6px ${color}`,
              }}
            />
          </Marker>
        ))}
      </Map>

      {/* address search (top, floating) */}
      <div style={{ position: 'absolute', top: 12, left: 12, width: 'min(380px, calc(100% - 24px))', zIndex: 3 }}>
        <AddressSearch onPick={importPolygon} />
      </div>

      {/* draw controls + status (bottom) */}
      <div
        style={{
          position: 'absolute',
          bottom: 12,
          left: 12,
          right: 12,
          zIndex: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <button type="button" onClick={() => setPoints((p) => p.slice(0, -1))} disabled={points.length === 0} style={ctrlBtn}>
          Undo
        </button>
        <button type="button" onClick={() => setPoints([])} disabled={points.length === 0} style={ctrlBtn}>
          Clear
        </button>
        <span
          className="mono"
          style={{
            marginLeft: 'auto',
            fontSize: 11,
            padding: '4px 10px',
            borderRadius: 6,
            background: 'rgba(15,21,30,0.85)',
            color: invalid ? DANGER : polygon ? 'var(--ok)' : 'var(--text-muted)',
          }}
        >
          {invalid && invalidMessage
            ? invalidMessage
            : polygon
              ? `${points.length} pts · ${formatAreaKmSq(area)}`
              : `${points.length} pts · need 3+`}
        </span>
      </div>
    </div>
  );
}

const ctrlBtn: React.CSSProperties = {
  background: 'rgba(15,21,30,0.85)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  color: 'var(--text-secondary)',
  fontSize: 12,
  padding: '5px 11px',
  cursor: 'pointer',
};
